import React from "react";
import { connect } from "react-redux";

// MUI Components
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import LinearProgress from "@material-ui/core/LinearProgress";
import Snackbar from "@material-ui/core/Snackbar";
import Slide from "@material-ui/core/Slide";

// Icons
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import IconButton from "@material-ui/core/IconButton";
// Custom Icons
import { TargetIcon, ThicknessIcon } from "../../constants/customIcons";

// Other
import { colors } from "../../constants/colors";
import { customTheme } from "../../constants/customTheme";
import SamplesList from "./SamplesList";
import BottomValueDisplay from "./BottomValueDisplay";
import { handleNullValues } from "../../constants/utilityFunctions";
import { percentage } from "../../constants/utilityFunctions";

// Actions
import {
  resetResultSnackbar,
  setMainViewSection,
  resetSampleSelection,
  setFocusElement,
} from "../../actions";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flex: "1 1 auto",
    minHeight: 0,
    width: "100%",
    overflow: "hidden",
  },
  locationName: {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    height: "35px",
    paddingTop: "3px",
    backgroundColor: (props) => colors[props.theme].locationListHeader,
  },
  titleBackArrow: {
    padding: "0px 19px 0px 36px",
  },
  deviceButton: (props) => ({
    height: "40px",
    ...customTheme(props.theme).buttonPrimary,
  }),
  icon: customTheme().icon,
  deviceButtonContainer: {
    margin: "10px",
    alignSelf: "center",
  },
  progressValueContainer: {
    boxShadow: (props) =>
      `0px 0px 10px 0px ${colors[props.theme].boxShadowColor}, 0.2)`,
    marginTop: "0px",
    paddingTop: "0px",
    display: "flex",
    height: "95px",
    justifyContent: "space-around",
  },
  progressValueContainerItem: {
    width: "50%",
    justifyContent: "center",
    marginTop: "5px",
    marginBottom: "5px",
    display: "flex",
    alignItems: "center",
  },
  progressBarText: {
    position: "absolute",
    zIndex: 1,
    fontSize: "1.1rem",
  },
  progressCSI: {
    width: "90%",
    zIndex: 0,
    backgroundImage: "none",
    backgroundColor: (props) => colors[props.theme].progressBarBkg,
    height: "25px",
    borderRadius: "4px",
    "& .MuiLinearProgress-dashed": {
      display: "none",
    },
    "& .MuiLinearProgress-barColorPrimary": {
      backgroundColor: (props) =>
        colors[props.theme].primaryProgressBarAcquired,
    },
    "& .MuiLinearProgress-bar2Buffer": {
      backgroundColor: (props) =>
        colors[props.theme].primaryProgressBarAcquired,
    },
  },
  progressBMODE: {
    width: "90%",
    zIndex: 0,
    backgroundImage: "none",
    backgroundColor: (props) => colors[props.theme].progressBarBkg,
    height: "25px",
    borderRadius: "4px",
    "& .MuiLinearProgress-dashed": {
      display: "none",
    },
    "& .MuiLinearProgress-barColorPrimary": {
      backgroundColor: (props) =>
        colors[props.theme].secondaryProgressBarAcquired,
    },
    "& .MuiLinearProgress-bar2Buffer": {
      backgroundColor: (props) =>
        colors[props.theme].secondaryProgressBarAcquired,
      // filter: "brightness(50%)",
    },
  },
  snackBarCsi: {
    width: "475px",
    "& .MuiSnackbarContent-root": {
      backgroundColor: (props) => colors[props.theme].primaryOverlay,
      padding: 0,
      backdropFilter: "blur(5px)",
      borderRadius: "8px",
    },
    "& .MuiSnackbar-anchorOriginTopCenter": {
      top: "30%",
    },
  },
  snackBarBmode: {
    width: "475px",
    "& .MuiSnackbarContent-root": {
      backgroundColor: (props) => colors[props.theme].secondaryOverlay,
      padding: 0,
      backdropFilter: "blur(5px)",
      borderRadius: "8px",
    },
    "& .MuiSnackbar-anchorOriginTopCenter": {
      top: "30%",
    },
  },
  message: {
    display: "flex",
    alignItems: "center",
    padding: "35px",
    lineHeight: "50px",
    fontSize: "6rem",
    fontWeight: 600,
  },
  iconMedium: customTheme().iconMedium,
});

const SamplesSection = (props) => {
  const classes = useStyles(props);

  const handleSwitchToLocationView = () => {
    props.dispatch(setMainViewSection("sampleFocused"));
    props.dispatch(resetSampleSelection());
    props.dispatch(setFocusElement(props.locationId));
  };

  const AcqTypeProgressBar = (type) => {
    return (
      <React.Fragment>
        <Typography className={classes.progressBarText}>
          {props.acqTypes[type].name} ({props.samplesAcquired[type]}/
          {props.samplesNeeded[type]})
        </Typography>
        <LinearProgress
          variant="buffer"
          value={percentage(
            props.samplesProcessed[type],
            props.samplesNeeded[type]
          )}
          valueBuffer={percentage(
            props.samplesAcquired[type],
            props.samplesNeeded[type]
          )}
          className={
            type === "CSI" ? classes.progressCSI : classes.progressBMODE
          }
        />
      </React.Fragment>
    );
  };

  const ShearWaveSpeed = () => {
    return (
      <BottomValueDisplay
        icon={<TargetIcon />}
        units="m/s"
        value={
          checkSampleCsi() ? handleNullValues(props.shearWaveSpeed.value) : null
        }
        standardDev={handleNullValues(props.shearWaveSpeed.standardDev)}
        sampleCount={props.samplesAcquired["CSI"]}
        color={colors[props.theme].primary}
        small={props.mainViewSectionRightOrientation}
        theme={props.theme}
      />
    );
  };

  const Thickness = () => {
    return (
      <BottomValueDisplay
        icon={<ThicknessIcon />}
        units="mm"
        value={handleNullValues(props.thickness.value)}
        standardDev={handleNullValues(props.thickness.standardDev)}
        sampleCount={props.samplesAcquired["BMODE"]}
        color={colors[props.theme].secondary}
        small={props.mainViewSectionRightOrientation}
        theme={props.theme}
      />
    );
  };

  const checkSampleCsi = () => {
    // check if any CSI samples for the location are not DONE
    let check = true;
    // go through every CSI sample
    for (let sampleId of props.samplesIdByTypeCSI) {
      if (
        props.samplesById[sampleId].locationId === props.locationId && // filter by just location
        !props.samplesById[sampleId].valueDisplay // ensure sample is finished processing
      ) {
        check = false;
      }
    }
    return check;
  };

  const AnySamplesNeeded = () => {
    let check = false;
    for (let type of Object.keys(props.acqTypes)) {
      if (props.samplesNeeded[type] > 0) {
        check = true;
      }
    }
    return check;
  };

  const AnySamplesAcquired = () => {
    let check = false;
    for (let type of Object.keys(props.acqTypes)) {
      if (props.samplesAcquired[type] > 0) {
        check = true;
      }
    }
    return check;
  };

  const acqTypeProgress = () => {
    return Object.keys(props.acqTypes).map((type) => {
      switch (type) {
        case "CSI":
          return (
            (props.samplesNeeded["CSI"] > 0 ||
              props.samplesProcessed["CSI"] > 0) &&
            (props.samplesProcessed["CSI"] >= props.samplesNeeded["CSI"] ? (
              <div key={"CSI"} className={classes.progressValueContainerItem}>
                <ShearWaveSpeed />
              </div>
            ) : (
              <div key={"CSI"} className={classes.progressValueContainerItem}>
                {AcqTypeProgressBar("CSI")}
              </div>
            ))
          );
        case "BMODE":
          return (
            (props.samplesNeeded["BMODE"] > 0 ||
              props.samplesProcessed["BMODE"] > 0) &&
            (props.samplesProcessed["BMODE"] >= props.samplesNeeded["BMODE"] ? (
              <div key={"BMODE"} className={classes.progressValueContainerItem}>
                <Thickness />
              </div>
            ) : (
              <div key={"BMODE"} className={classes.progressValueContainerItem}>
                {AcqTypeProgressBar("BMODE")}
              </div>
            ))
          );

        case "PICTURE":
          return (
            (props.samplesNeeded["PICTURE"] > 0 ||
              props.samplesProcessed["PICTURE"] > 0) &&
            props.samplesProcessed["PICTURE"] <
              props.samplesNeeded["PICTURE"] && (
              <div
                key={"PICTURE"}
                className={classes.progressValueContainerItem}
              >
                {AcqTypeProgressBar("PICTURE")}
              </div>
            )
          );
        default:
          return;
      }
    });
  };

  return (
    <div className={classes.root}>
      {/* LOCATION NAME */}
      <div className={classes.locationName}>
        {props.mainViewSectionRightOrientation && (
          <IconButton
            className={classes.titleBackArrow}
            onClick={() => handleSwitchToLocationView()}
          >
            <KeyboardArrowLeft className={classes.iconMedium} />
          </IconButton>
        )}
      </div>

      {/* SAMPLES */}
      <SamplesList />

      {/*PROGRESS BARS / AVERAGE VALUES */}
      {(AnySamplesNeeded() || AnySamplesAcquired()) && (
        //  Display section if any samples are needed
        <div className={classes.progressValueContainer}>
          {acqTypeProgress()}
        </div>
      )}

      {/* RESULT SNACKBAR */}
      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        onClose={() => props.dispatch(resetResultSnackbar())}
        TransitionComponent={Slide}
        open={props.samplesResultSnackbar.status}
        autoHideDuration={3000} // 3 second duration
        disableWindowBlurListener={true}
        className={
          props.samplesResultSnackbar.sampleType === "CSI"
            ? classes.snackBarCsi
            : classes.snackBarBmode
        }
        message={
          <Typography
            color={
              props.samplesResultSnackbar.sampleType === "CSI"
                ? "primary"
                : "secondary"
            }
            variant="h6"
            className={classes.message}
          >
            {props.samplesResultSnackbar.value}
            {props.samplesResultSnackbar.sampleType === "CSI" ? " m/s" : " mm"}
          </Typography>
        }
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  const selectedLocation =
    state.locations.byId[state.locations.selectedLocationId];

  return {
    locationName: selectedLocation.name,
    locationId: selectedLocation.id,
    samplesAcquired: selectedLocation.samplesAcquired,
    samplesProcessed: selectedLocation.samplesProcessed,
    samplesNeeded: selectedLocation.samplesNeeded,
    shearWaveSpeed: selectedLocation.shearWaveSpeed,
    thickness: selectedLocation.thickness,
    locked: state.studyInfo.locked,
    acqTypes: state.protocol.acq_types,
    samplesIdByTypeCSI: state.samples.idByType["CSI"],
    samplesResultSnackbar: state.utility.resultSnackbar,
    mainViewSectionRightOrientation:
      state.view.mainViewSection.orientation === "RIGHT",
    samplesById: state.samples.byId, // this object could be slimmed down to minimize rerenders
    theme: state.utility.theme,
  };
};

export default connect(mapStateToProps)(SamplesSection);
